/**
 * Signing a device out remotely ends its server session, so every request from
 * that device is refused. This module is how the browser's other tabs of that
 * same session find out at once instead of each one failing on its own next
 * request: the tab that learns the session is gone announces its id through
 * localStorage, and any tab holding that same session signs itself out.
 */

export const REVOKED_KEY = "hrms-session-revoked";

/** The server-issued session id inside an access token (`sid`), or null. The
 * token is read, not trusted: the server is what decides whether a session is
 * alive; this only lets tabs recognise "the session that just ended is mine". */
export function sessionIdOf(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;
  const part = accessToken.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    const sid = (JSON.parse(json) as { sid?: unknown }).sid;
    return typeof sid === "string" && sid ? sid : null;
  } catch {
    return null;
  }
}

/** Tells the other tabs that this session has ended. */
export function announceRevokedSignOut(accessToken: string | null | undefined): void {
  const sid = sessionIdOf(accessToken);
  if (!sid) return;
  try {
    // The timestamp makes every announcement a new value, so the storage
    // event fires even if the same session were announced twice.
    localStorage.setItem(REVOKED_KEY, JSON.stringify({ sid, at: Date.now() }));
  } catch {
    // Storage blocked: the other tabs simply find out on their own next request.
  }
}

/** Reads the session id out of a storage event for REVOKED_KEY, or null. */
export function revokedSessionFrom(event: Pick<StorageEvent, "key" | "newValue">): string | null {
  if (event.key !== REVOKED_KEY || !event.newValue) return null;
  try {
    const sid = (JSON.parse(event.newValue) as { sid?: unknown }).sid;
    return typeof sid === "string" && sid ? sid : null;
  } catch {
    return null;
  }
}
